<template>
  <div class="vsd-report break-inside-avoid">
    <p class="heading-2 my-3">{{ scenario?.title }}</p>

    <UseScenarioUnits
      v-slot="{
        getLargeLengthUnit,
        getApparentPowerUnit,
        getVoltageUnit,
        getCurrentUnit,
        getTempUnit
      }"
      :scenario="scenario"
    >
      <div class="outer">
        <div class="input-data">
          <div class="input-data__header">Input Data</div>

          <div class="input-data__block">
            <h5 class="input-data__title">Motor Nameplate:</h5>
            <h6 class="equipment-label">
              {{ computeMotorLabel(scenario?.equipment?.r_motor) }}
            </h6>
            <div class="input-data__values">
              <div
                class="input-data__item"
                :style="{
                  maxWidth: '33%'
                }"
              >
                <span class="input-data__label">HP</span>
                <ConverterInput
                  :decimalPlaces="0"
                  disable-label
                  disable-suffix
                  class="input-data__input"
                  :base-unit="PowerUnits.HorsePower"
                  :model="PowerUnits.options()"
                  mode="read"
                  :model-value="
                    scenarioCalculations.motorCalculations
                      .adjustedReratedNameplateHP.value
                  "
                />
              </div>
              <div
                class="input-data__item"
                :style="{
                  maxWidth: '33%'
                }"
              >
                <span class="input-data__label">{{ voltageLabel }}</span>
                <ConverterInput
                  disable-label
                  disable-suffix
                  :decimalPlaces="0"
                  class="input-data__input"
                  :base-unit="VoltageUnits.Volts"
                  :target-unit="getVoltageUnit('Surface Voltage.convert.V')"
                  :model="VoltageUnits.options()"
                  mode="read"
                  :model-value="
                    scenarioCalculations.motorCalculations
                      .adjustedReratedNameplateV.value
                  "
                />
              </div>
              <div
                class="input-data__item"
                :style="{
                  maxWidth: '33%'
                }"
              >
                <span class="input-data__label">{{ currentLabel }}</span>
                <ConverterInput
                  :decimalPlaces="0"
                  disable-label
                  disable-suffix
                  class="input-data__input"
                  :base-unit="CurrentUnits.Amperes"
                  :target-unit="getCurrentUnit('Motor Current.convert.A')"
                  :model="CurrentUnits.options()"
                  mode="read"
                  :model-value="
                    scenarioCalculations.motorCalculations
                      .adjustedReratedNameplateA.value
                  "
                />
              </div>
            </div>
          </div>
          <hr />

          <div class="input-data__block">
            <div class="input-data__values">
              <div class="input-data__item">
                <span class="input-data__label">Max {{ frequencyLabel }}</span>
                <ConverterInput
                  disable-label
                  disable-suffix
                  class="input-data__input"
                  :base-unit="FrequencyUnits.Hertz"
                  :target-unit="FrequencyUnits.Hertz"
                  :model="FrequencyUnits.options()"
                  mode="read"
                  :model-value="scenario?.parameters?.maximum_frequency"
                />
              </div>
              <div class="input-data__item">
                <span class="input-data__label">Min {{ frequencyLabel }}</span>
                <ConverterInput
                  disable-label
                  disable-suffix
                  class="input-data__input"
                  :base-unit="FrequencyUnits.Hertz"
                  :target-unit="FrequencyUnits.Hertz"
                  :model="FrequencyUnits.options()"
                  mode="read"
                  :model-value="scenario?.parameters?.minimum_frequency"
                />
              </div>
            </div>
          </div>
          <hr />

          <div v-if="isScenarioEspShore" class="input-data__block">
            <h5 class="input-data__title">Cable Info:</h5>
            <div
              v-for="(cable, i) in formattedCables"
              :key="cable.id"
              class="cable-info"
              :class="{ isLast: i === formattedCables.length - 1 }"
            >
              <h6 class="equipment-label">
                {{ cable.label }}
                <Badge
                  v-if="cable.isSurface"
                  class="cable-surface-badge"
                  severity="success"
                  value="SURFACE"
                />
              </h6>
              <div class="input-data__values">
                <div class="input-data__item">
                  <span class="input-data__label">AWG</span>
                  <span>{{ cable.gauge }}</span>
                </div>
                <div class="input-data__item">
                  <span class="input-data__label">Length</span>
                  <ConverterInput
                    disable-label
                    force-horizontal
                    use-long-label
                    class="input-data__input"
                    :base-unit="LargeLengthUnits.Feet"
                    :target-unit="getLargeLengthUnit('Length.convert.Feet')"
                    :model="LargeLengthUnits.options()"
                    mode="read"
                    :model-value="cable.length"
                  />
                </div>
              </div>
            </div>
            <hr />
            <div class="input-data__values">
              <div>
                <span :style="{ whiteSpace: 'pre' }">Downhole Temp</span>
                <ConverterInput
                  disable-label
                  use-long-label
                  force-horizontal
                  class="input-data__input"
                  :base-unit="TemperatureUnits.Fahrenheit"
                  :target-unit="getTempUnit('Surface Temperature.write.F')"
                  :model="LargeLengthUnits.options()"
                  mode="read"
                  :model-value="
                    scenarioCalculations.bottomholeTemperatureF.value
                  "
                />
              </div>
              <div>
                <span :style="{ whiteSpace: 'pre' }">Motor Rated RPM</span>
                <ConverterInput
                  disable-label
                  disable-suffix
                  class="input-data__input"
                  :base-unit="LargeLengthUnits.Feet"
                  :target-unit="getLargeLengthUnit('Length.convert.Feet')"
                  :model="LargeLengthUnits.options()"
                  mode="read"
                  :model-value="
                    Math.round(
                      scenarioCalculations.motorCalculations.motorRatedRPM.value
                    )
                  "
                />
              </div>
            </div>
          </div>
        </div>
        <div class="input-data">
          <div class="input-data__header">Set Up For VSD</div>
          <div>
            <table class="vsd-report-table">
              <tr>
                <td>1</td>
                <td>Voltage Drop ({{ voltageLabel }})</td>
                <td></td>
                <td>
                  <ConverterInput
                    disable-label
                    disable-suffix
                    condensed
                    :base-unit="VoltageUnits.Volts"
                    :target-unit="getVoltageUnit('Surface Voltage.convert.V')"
                    :model="VoltageUnits.options()"
                    mode="read"
                    :model-value="
                      Math.ceil(
                        scenarioCalculations.cableCalculations.aggregate
                          .totalCablesVoltLossAtNameplateAtMaxHzV.value
                      )
                    "
                  />
                </td>
              </tr>

              <tr>
                <td>2</td>
                <td>
                  {{ voltageLabel }} @ ({{
                    scenario?.parameters?.maximum_frequency
                  }}{{ frequencyLabel }}) Max {{ frequencyLabel }}
                </td>
                <td></td>
                <td>
                  <ConverterInput
                    disable-label
                    disable-suffix
                    condensed
                    :base-unit="VoltageUnits.Volts"
                    :target-unit="getVoltageUnit('Surface Voltage.convert.V')"
                    :model="VoltageUnits.options()"
                    mode="read"
                    :model-value="
                      Math.ceil(
                        scenarioCalculations.surfaceDriveCalculations
                          .requiredVoltageAtMaxHz60HzV.value
                      )
                    "
                  />
                </td>
              </tr>
              <tr>
                <td>3</td>
                <td>Available {{ voltageLabel }}</td>
                <td></td>
                <td>
                  <ConverterInput
                    disable-label
                    disable-suffix
                    condensed
                    :base-unit="VoltageUnits.Volts"
                    :target-unit="getVoltageUnit('Surface Voltage.convert.V')"
                    :model="VoltageUnits.options()"
                    mode="read"
                    :model-value="
                      scenarioCalculations.surfaceDriveCalculations
                        .transformerTapData.value.availableVoltage
                    "
                  />
                </td>
              </tr>
              <tr>
                <td>4</td>
                <td>Xmfr Ratio</td>
                <td></td>
                <td>
                  <ConverterInput
                    disable-label
                    disable-suffix
                    condensed
                    :base-unit="GenericUnits.Generic"
                    :target-unit="GenericUnits.Generic"
                    :model="[GenericUnits.Generic]"
                    mode="read"
                    :decimal-places="2"
                    :model-value="
                      scenarioCalculations.surfaceDriveCalculations
                        .transformerRatio.value
                    "
                  />
                </td>
              </tr>
              <tr>
                <td>5</td>
                <td>{{ voltageLabel }} @ 60 Hz</td>
                <td></td>
                <td>
                  <ConverterInput
                    disable-label
                    disable-suffix
                    condensed
                    :base-unit="VoltageUnits.Volts"
                    :target-unit="getVoltageUnit('Surface Voltage.convert.V')"
                    :model="VoltageUnits.options()"
                    mode="read"
                    :model-value="
                      Math.ceil(
                        scenarioCalculations.surfaceDriveCalculations
                          .requiredVoltageAtMaxHz60HzV.value
                      )
                    "
                  />
                </td>
              </tr>
              <tr>
                <td>6</td>
                <td>VSD {{ voltageLabel }} @ 60 Hz</td>
                <td></td>
                <td>
                  <ConverterInput
                    disable-label
                    disable-suffix
                    condensed
                    :base-unit="VoltageUnits.Volts"
                    :target-unit="getVoltageUnit('Surface Voltage.convert.V')"
                    :model="VoltageUnits.options()"
                    mode="read"
                    :model-value="
                      Math.round(
                        scenarioCalculations.motorCalculations
                          .maxVSDOutputVoltage60HzV.value
                      )
                    "
                  />
                </td>
              </tr>
              <tr>
                <td>7</td>
                <td>I Limit</td>
                <td></td>
                <td>
                  <ConverterInput
                    disable-label
                    disable-suffix
                    condensed
                    :base-unit="CurrentUnits.Amperes"
                    :target-unit="CurrentUnits.Amperes"
                    :model="CurrentUnits.options()"
                    mode="read"
                    :model-value="
                      scenarioCalculations.surfaceDriveCalculations.iLimitA
                        .value
                    "
                  />
                </td>
              </tr>
              <tr>
                <td>8</td>
                <td>Underload</td>
                <td></td>
                <td>
                  <ConverterInput
                    disable-label
                    disable-suffix
                    condensed
                    :base-unit="CurrentUnits.Amperes"
                    :target-unit="getCurrentUnit()"
                    :model="CurrentUnits.options()"
                    mode="read"
                    :model-value="
                      scenarioCalculations.surfaceDriveCalculations
                        .vsdUnderloadA.value
                    "
                  />
                </td>
              </tr>
              <tr>
                <td>9</td>
                <td>Overload</td>
                <td></td>
                <td>
                  <ConverterInput
                    disable-label
                    disable-suffix
                    condensed
                    :base-unit="CurrentUnits.Amperes"
                    :target-unit="getCurrentUnit()"
                    :model="CurrentUnits.options()"
                    mode="read"
                    :model-value="
                      scenarioCalculations.surfaceDriveCalculations.vsdOverloadA
                        .value
                    "
                  />
                </td>
              </tr>

              <tr>
                <td>10</td>
                <td>I Limit Sync</td>
                <td></td>
                <td>
                  <ConverterInput
                    disable-label
                    disable-suffix
                    condensed
                    :base-unit="CurrentUnits.Amperes"
                    :target-unit="getCurrentUnit()"
                    :model="CurrentUnits.options()"
                    mode="read"
                    :model-value="
                      scenarioCalculations.surfaceDriveCalculations.iLimitSync
                        .value
                    "
                  />
                </td>
              </tr>

              <tr>
                <td>11</td>
                <td>
                  {{
                    getApparentPowerUnit(
                      "Surface kVA.convert.kVA"
                    ).value?.label?.toUpperCase() || "KVA"
                  }}
                </td>
                <td></td>
                <td>
                  <ConverterInput
                    disable-label
                    disable-suffix
                    condensed
                    :base-unit="ApparentPowerUnits.KiloVoltAmps"
                    :target-unit="
                      getApparentPowerUnit('Surface kVA.convert.kVA')
                    "
                    :model="ApparentPowerUnits.options()"
                    mode="read"
                    :model-value="
                      scenarioCalculations.surfaceDriveCalculations.kvaAtMaxHz
                        .value
                    "
                  />
                </td>
              </tr>
            </table>
          </div>
        </div>

        <!-- VSD Setup Table -->
      </div>
    </UseScenarioUnits>
  </div>
</template>

<script setup lang="ts">
import ConverterInput from "@/components/ConverterInput.vue";
import UseScenarioUnits from "@/composables/units/UseScenarioUnits.vue";

import { LargeLengthUnits } from "@/libs/units/length";
import { FrequencyUnits } from "@/libs/units/frequency";
import { PowerUnits } from "@/libs/units/power";
import { VoltageUnits } from "@/libs/units/voltage";
import { GenericUnits } from "@/libs/units/generic";
import { CurrentUnits } from "@/libs/units/current";
import { ApparentPowerUnits } from "@/libs/units/apparentPower";
import { TemperatureUnits } from "@/libs/units/temperature";

import { computed, toRefs } from "vue";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { Scenario } from "@/composables/forms/formTypes";
import { computeCableLabel } from "@/models/equipment/cables/properties";
import { computeMotorLabel } from "@/models/equipment/motors/properties";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { sizingKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { isSurfaceCable } from "@/libs/calculations/cables";
import { Sizing_Pumping_Type_Enum } from "gql/api";

const sizing = injectStrict(sizingKey, null);
const props = defineProps<{
  scenario?: Scenario | null;
  scenarioCalculations: ScenarioCalculations;
  removePageBreak?: boolean;
}>();

const { scenario } = toRefs(props);

const scenarioUnits = useScenarioUnits(scenario, sizing);

const voltageLabel = computed(() => {
  const v = scenarioUnits.getVoltageUnit("Surface Voltage.convert.V");
  if (v?.value?.longLabel) {
    return v.value.longLabel;
  }
  return v?.value.label || "";
});
const frequencyLabel = computed(() => {
  return FrequencyUnits.Hertz.shortLabel ?? FrequencyUnits.Hertz.label;
});
const currentLabel = computed(() => {
  const v = scenarioUnits.getCurrentUnit("Motor Current.convert.A");
  if (v?.value?.longLabel) {
    return v.value.longLabel;
  }
  return v?.value.label || "";
});

const isScenarioEspShore = computed(() => {
  return props.scenario?.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore;
});
const formattedCables = computed(() => {
  return props.scenarioCalculations.orderedCables?.value.map(cable => {
    const cableCalcs =
      props.scenarioCalculations.cableCalculations.individual.value[cable.id];
    return {
      id: cable.id,
      isSurface: isSurfaceCable(cable.id),
      label: computeCableLabel(cable.r_cable),
      length: cableCalcs.cableLengthFT,
      gauge: cableCalcs.xrefCable?.r_cable?.gauge ?? "—"
    };
  });
});
</script>

<style lang="scss" scoped>
.vsd-report {
  &:not(:last-child) {
    margin-bottom: 20px;
  }
}
.input-data {
  outline: 1px solid var(--gray-400);
  margin-left: 10px;
  margin-right: 10px;
  > * {
    margin-left: 10px;
  }
}
.input-data__header {
  margin: 0px;
  margin-bottom: 15px;
  height: 50px;
  padding-left: 10px;
  display: flex;
  align-items: center;
  background-color: var(--surface-200) !important;
  font-size: 1.2rem;
}
.input-data__title {
  overflow: hidden;
  display: inline-block;
  font-size: 1.1rem;
  padding-bottom: 0.1em;
  border-bottom: 1px solid var(--gray-400);
  margin: 0px;
  margin-top: 5px;
  &:not(:last-child) {
    margin-bottom: 5px;
  }
}
.equipment-label {
  font-size: 0.9rem;
  color: var(--gray-700);
  margin: 0px;
}
.cable-info {
  margin-bottom: 5px;
  border-bottom: none;
  &:not(.isLast) {
    border-bottom: 1px solid var(--gray-400);
    margin-bottom: 8px;
  }
  .cable-surface-badge {
    margin-left: 5px;
  }
}
.input-data__values {
  background-color: var(--surface-700) !important;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
}
.input-data__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.3rem; // space between label and input
  box-sizing: border-box;
}

.input-data__label {
  margin: 0; // remove bottom margin from the column layout
  white-space: nowrap;
  font-weight: 600;
}

.input-data__input {
  flex: 1; // let the input grow to fill the remaining space
  max-width: 100%;
}

.outer {
  display: flex;
  flex-direction: row;
  > div {
    width: auto;
    max-width: calc(100% - 280px);
    &:nth-child(2) {
      overflow-x: hidden;
      width: 50%;
      min-width: 280px;
    }
  }
}
.svg-inline--fa {
  color: var(--danger-500);
}
.vsd-report-table {
  margin-bottom: 40px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
  &:not(:first-of-type) {
    break-inside: avoid;
  }
  td {
    &:first-of-type {
      padding-right: 8px;
    }
  }
}

tr {
  break-inside: avoid;
}
td {
  padding-top: 3px;
  padding-bottom: 3px;
  &:nth-child(2) {
    min-width: 100px;
  }
}

td,
tr {
  background-color: var(--surface-700) !important;
}
table {
  border-collapse: collapse;
}

tr {
  span {
    font-size: 1rem !important;
  }
  font-size: 1rem;
  td {
    font-size: 0.9rem;
    border-bottom: 1px solid var(--gray-600);
  }
}
tr:last-child td {
  border-bottom: none;
}

// all rows that are data content
tr:not(.header-row):not(.title-row) {
  font-weight: 300;
  & > td:not(:first-of-type) {
    font-weight: 500;
  }
}

// well info section headers
tr.title-row {
  background-color: $hover-color;
  // height: 24px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: -0.5px;
}
@media screen {
  [data-theme="dark"] {
    .equipment-label {
      font-size: 0.9rem;
      color: var(--gray-400);
      margin: 0px;
    }
  }
}
</style>
